// src/pages/ViewQuotePage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext'; 
import { formatRut, formatCurrency, formatPhone } from '../utils/formatters';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';

// Servicios y Hooks
import { quoteService } from '../services/quoteService';
import { usePermissions } from '../hooks/usePermissions';

// Iconos
import { PencilSquareIcon, TrashIcon, EnvelopeIcon, PrinterIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/solid';

// --- COMPONENTES AUXILIARES DE DISEÑO ---

const ReadOnlyField = ({ label, value, fullWidth = false }) => (
    <div className={fullWidth ? 'md:col-span-3 print:col-span-3' : ''}>
        <p className="block text-xs uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 print:text-gray-500 mb-1">{label}</p>
        <div className="text-sm text-gray-900 dark:text-gray-100 print:text-gray-900 bg-gray-50 dark:bg-gray-800 print:bg-gray-50 p-2 rounded border border-gray-100 dark:border-gray-700 print:border-gray-200 min-h-[2.5rem] whitespace-pre-wrap flex items-center transition-colors">
            {value || '-'}
        </div>
    </div>
);

// --- Componente Modal para Enviar Correo ---
const EmailModal = ({ quote, quoteId, onClose, setNotification }) => {
    const { user } = useAuth(); 
    const [recipient, setRecipient] = useState(quote.email || '');
    const [cc, setCc] = useState('');
    const [sending, setSending] = useState(false);
    const [sendingMessage, setSendingMessage] = useState('Enviar');

    const handleSendEmail = async (e) => {
        e.preventDefault();
        setSending(true);
        setSendingMessage('Enviando Correo...');

        const payload = {
            reportId: quoteId,
            emailTo: recipient,
            emailCc: cc,
            emailSubject: `Cotización Comercial #${quoteId} - ${user.displayName || 'BantCore'}`,
            emailBody: `
                <div style="font-family: Arial, sans-serif; padding: 20px;">
                    <h2>Cotización Comercial Asignada</h2>
                    <p>Estimado/a <b>${quote.clientName}</b>,</p>
                    <p>Adjuntamos a este correo la cotización formal folio <b>#${quoteId}</b> generada el ${new Date(quote.createdAt.seconds * 1000).toLocaleDateString('es-CL')}.</p>
                    <p>Ante cualquier duda, puede responder directamente a este correo o contactarse a <strong>${user.email}</strong>.</p>
                    <br><p>Saludos cordiales,<br><b>BantCore System</b></p>
                </div>
            `,
            isQuote: true
        };

        try {
            const generateAndSendPDF = httpsCallable(functions, 'generateAndSendPDF');
            const result = await generateAndSendPDF(payload);
            setNotification({ message: result.data.message || 'Cotización enviada exitosamente como PDF adjunto.', type: 'success' });
            onClose();
        } catch (error) {
            console.error('Error enviando cotización por Cloud Functions:', error);
            setNotification({ message: `Error al enviar: ${error.message || 'Revisa la configuración del servidor.'}`, type: 'error' });
        } finally {
            setSending(false);
            setSendingMessage('Enviar');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 print:hidden backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-lg p-8 w-full max-w-lg shadow-xl border border-transparent dark:border-gray-700 transition-colors">
                <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">Enviar Cotización por Correo</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Enviando como: <span className="font-semibold">{user.email}</span>
                </p>
                <form onSubmit={handleSendEmail}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Para (Cliente):</label>
                        <input type="email" value={recipient} onChange={e => setRecipient(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-blue-500 transition-colors" required />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">CC (Copia a):</label>
                        <input type="email" value={cc} onChange={e => setCc(e.target.value)} placeholder="correo@ejemplo.com" className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-blue-500 transition-colors" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mb-6">Una copia oculta de respaldo se enviará a la administración.</p>
                    <div className="flex justify-end space-x-4">
                        <button type="button" onClick={onClose} className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Cancelar</button>
                        <button type="submit" disabled={sending} className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:bg-blue-300 disabled:dark:bg-blue-800 hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors">
                            {sending ? sendingMessage : 'Enviar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Componente de Contenido Reutilizable ---
const QuoteContent = ({ quote, quoteId, parameters, itemsWithCalculatedPrice, includeBankDetails = true }) => (
    <div className="space-y-6">
        {/* SECCIÓN I: DATOS CLIENTE */}
        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:border print:border-gray-200 break-inside-avoid transition-colors">
            <h2 className="text-sm font-bold mb-6 pb-2 text-blue-800 dark:text-blue-400 print:text-blue-800 uppercase border-b-2 border-blue-100 dark:border-blue-900 print:border-blue-100 flex items-center gap-2">
                <span className="bg-blue-100 dark:bg-blue-900/50 print:bg-blue-100 text-blue-800 dark:text-blue-300 print:text-blue-800 w-6 h-6 flex items-center justify-center rounded-full text-xs">I</span>
                Datos del Cliente y Condiciones
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-6 print:gap-4">
                <ReadOnlyField label="Fecha Emisión" value={quote.createdAt?.seconds ? new Date(quote.createdAt.seconds * 1000).toLocaleDateString('es-CL') : 'N/A'} />
                <ReadOnlyField label="Cliente / Empresa" value={quote.clientName} />
                <ReadOnlyField label="RUT" value={formatRut(quote.clientId)} />

                <ReadOnlyField label="Dirección" value={quote.address} fullWidth />

                <ReadOnlyField label="Atención A" value={quote.encargado} />
                <ReadOnlyField label="Teléfono" value={formatPhone(quote.phone)} />
                <ReadOnlyField label="Email" value={quote.email} />

                <ReadOnlyField label="Forma de Pago" value={quote.paymentMethod} />
                <ReadOnlyField label="Tiempo de Entrega" value={quote.entrega} />
                <ReadOnlyField label="Validez Oferta" value={quote.validez} />
            </div>
        </div>

        {/* SECCIÓN II: ÍTEMS */}
        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:border print:border-gray-200 transition-colors">
            <h2 className="text-sm font-bold mb-6 pb-2 text-blue-800 dark:text-blue-400 print:text-blue-800 uppercase border-b-2 border-blue-100 dark:border-blue-900 print:border-blue-100 flex items-center gap-2">
                <span className="bg-blue-100 dark:bg-blue-900/50 print:bg-blue-100 text-blue-800 dark:text-blue-300 print:text-blue-800 w-6 h-6 flex items-center justify-center rounded-full text-xs">II</span>
                Detalle de la Cotización
            </h2>
            
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 print:border-gray-200 transition-colors">
                <table className="min-w-full text-sm text-left">
                    <thead className="bg-gray-50 dark:bg-gray-700 print:bg-gray-50 text-gray-700 dark:text-gray-300 print:text-gray-700 font-medium transition-colors">
                        <tr>
                            <th className="px-4 py-3 border-b dark:border-gray-600 print:border-gray-200 w-16 text-center">Cant.</th>
                            <th className="px-4 py-3 border-b dark:border-gray-600 print:border-gray-200 w-16 text-center">Unid.</th>
                            <th className="px-4 py-3 border-b dark:border-gray-600 print:border-gray-200">Descripción</th>
                            <th className="px-4 py-3 border-b dark:border-gray-600 print:border-gray-200 text-right w-32">P. Unitario</th>
                            <th className="px-4 py-3 border-b dark:border-gray-600 print:border-gray-200 text-right w-32">Total</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700 print:divide-gray-200 transition-colors">
                        {itemsWithCalculatedPrice.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/50">
                                <td className="px-4 py-3 text-center text-gray-900 dark:text-gray-100 print:text-gray-900">{item.quantity}</td>
                                <td className="px-4 py-3 text-center text-gray-500 dark:text-gray-400 print:text-gray-500 text-xs">{item.unit}</td>
                                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 print:text-gray-700">{item.description}</td>
                                <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400 print:text-gray-600">{formatCurrency(item.price)}</td>
                                <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-gray-100 print:text-gray-900">{formatCurrency(item.price * item.quantity)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-end mt-4 break-inside-avoid">
                <div className="w-full md:w-1/3 print:w-1/3 space-y-2 bg-gray-50 dark:bg-gray-900 print:bg-gray-50 p-4 rounded-lg border border-gray-100 dark:border-gray-700 print:border-gray-200 transition-colors">
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400 print:text-gray-600">Neto:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100 print:text-gray-900">{formatCurrency(quote.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400 print:text-gray-600">IVA ({parameters.iva}%):</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100 print:text-gray-900">{formatCurrency(quote.ivaAmount)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t border-gray-300 dark:border-gray-600 print:border-gray-300 pt-2 mt-2 text-blue-900 dark:text-blue-400 print:text-blue-900 transition-colors">
                        <span>Total:</span>
                        <span>{formatCurrency(quote.total)}</span>
                    </div>
                </div>
            </div>
        </div>

        {/* SECCIÓN III: DETALLES */}
        {quote.details && (
            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:border print:border-gray-200 break-inside-avoid transition-colors">
                <h2 className="text-sm font-bold mb-6 pb-2 text-blue-800 dark:text-blue-400 print:text-blue-800 uppercase border-b-2 border-blue-100 dark:border-blue-900 print:border-blue-100 flex items-center gap-2">
                    <span className="bg-blue-100 dark:bg-blue-900/50 print:bg-blue-100 text-blue-800 dark:text-blue-300 print:text-blue-800 w-6 h-6 flex items-center justify-center rounded-full text-xs">III</span>
                    Notas y Observaciones
                </h2>
                <div className="text-sm text-gray-700 dark:text-gray-300 print:text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {quote.details}
                </div>
            </div>
        )}

        {/* SECCIÓN IV: FOTOS */}
        {quote.fotos && quote.fotos.length > 0 && (
             <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:border print:border-gray-200 transition-colors">
                <h2 className="text-sm font-bold mb-6 pb-2 text-gray-700 dark:text-gray-300 print:text-gray-700 border-b dark:border-gray-700 print:border-gray-200 flex items-center gap-2">
                    <span className="bg-gray-200 dark:bg-gray-700 print:bg-gray-200 text-gray-700 dark:text-gray-300 print:text-gray-700 w-6 h-6 flex items-center justify-center rounded-full text-xs">IV</span>
                    Imágenes Referenciales
                </h2>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 print:grid-cols-4 gap-4">
                    {quote.fotos.map((photo, index) => (
                        <div key={index} className="relative border border-gray-200 dark:border-gray-700 print:border-gray-200 p-1 rounded-lg bg-gray-50 dark:bg-gray-900 print:bg-gray-50 break-inside-avoid transition-colors">
                            <a href={photo.url} target="_blank" rel="noopener noreferrer" className="block aspect-video overflow-hidden rounded-md">
                                <img src={photo.url} alt={`ref ${index + 1}`} className="w-full h-full object-cover rounded-md"/>
                            </a>
                            <p className="text-center text-[10px] text-gray-500 dark:text-gray-400 print:text-gray-500 mt-1">Ref. {index + 1}</p>
                        </div>
                    ))}
                </div>
            </div>
        )}

         {parameters.bankName && includeBankDetails && (
            <div className="bg-gray-50 dark:bg-gray-900 print:bg-gray-50 p-6 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 print:border-gray-300 break-inside-avoid transition-colors">
                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 print:text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 print:bg-gray-400 rounded-full"></span>
                    Datos para Transferencia
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 print:grid-cols-2 gap-x-8 gap-y-1 text-xs text-gray-600 dark:text-gray-400 print:text-gray-600">
                    <p><strong className="text-gray-800 dark:text-gray-200 print:text-gray-800">Beneficiario:</strong> {parameters.bankBeneficiaryName}</p>
                    <p><strong className="text-gray-800 dark:text-gray-200 print:text-gray-800">RUT:</strong> {formatRut(parameters.bankRut)}</p>
                    <p><strong className="text-gray-800 dark:text-gray-200 print:text-gray-800">Banco:</strong> {parameters.bankName}</p>
                    <p><strong className="text-gray-800 dark:text-gray-200 print:text-gray-800">Cuenta:</strong> {parameters.bankAccount} ({parameters.bankAccountType})</p>
                    <p><strong className="text-gray-800 dark:text-gray-200 print:text-gray-800">Email:</strong> {parameters.bankEmail}</p>
                </div>
            </div>
         )}
    </div>
);

// --- Componente Principal ---
const ViewQuotePage = ({ quoteId, setPage, setNotification }) => {
    const { user } = useAuth();
    const { logoUrl, watermarkUrl, address: companyAddress, email: companyEmail, phone: companyPhone, name: contextCompanyName } = useCompany();
    const { canDo, isAdmin } = usePermissions();
    
    const [quoteData, setQuoteData] = useState(null);
    const [parameters, setParameters] = useState({});
    const [loading, setLoading] = useState(true);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [includeBankDetails, setIncludeBankDetails] = useState(true);
    
    const displayCompanyName = contextCompanyName || parameters.companyName || 'NOMBRE EMPRESA';

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                const [quote, params] = await Promise.all([
                    quoteService.getById(quoteId),
                    quoteService.getSystemParams()
                ]);
                
                if (quote) {
                    setQuoteData(quote);
                } else {
                    setNotification({ message: 'Cotización no encontrada.', type: 'error' });
                    setPage('quotes');
                }
                setParameters(params || {});

            } catch (error) {
                console.error("Error fetching data:", error);
                setNotification({ message: 'Error al cargar los datos.', type: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, [quoteId, setNotification, setPage]);

    // Recalcular precios para visualización
    const itemsWithCalculatedPrice = useMemo(() => {
        if (!quoteData || !quoteData.items || !parameters) return [];
        return quoteData.items.map(item => {
            const cost = parseFloat(item.cost) || 0;
            const overhead = parseFloat(parameters.gastosFijos || 0) / 100;
            const profit = parseFloat(parameters.utilidad || 0) / 100;
            const calculatedPrice = cost * (1 + overhead + profit);
            return { ...item, price: item.price || calculatedPrice };
        });
    }, [quoteData, parameters]);

    const handleDelete = async () => {
        if (!window.confirm(`¿Seguro que quieres eliminar la cotización ${quoteId}?`)) return;
        try {
            await quoteService.deleteQuote(quoteId, quoteData.fotos);
            setNotification({ message: `Cotización ${quoteId} eliminada.`, type: 'success' });
            setPage('quotes');
        } catch (error) {
            setNotification({ message: 'Error al eliminar la cotización.', type: 'error' });
        }
    };

    const handleApprove = async () => {
         if (!window.confirm(`¿Estás seguro de que quieres aprobar la cotización ${quoteId}? Se creará un registro contable.`)) return;
         try {
             await quoteService.approveQuote(quoteId, quoteData);
             setQuoteData(prev => ({ ...prev, status: 'Aprobada' }));
             setNotification({ message: `Cotización ${quoteId} aprobada.`, type: 'success' });
         } catch (error) {
             setNotification({ message: 'Error al aprobar la cotización.', type: 'error' });
         }
    };

    if (loading) return <div className="p-8 text-center text-gray-700 dark:text-gray-300">Cargando cotización...</div>;
    if (!quoteData) return <div className="p-8 text-center text-gray-700 dark:text-gray-300">No se pudo cargar la cotización.</div>;

    return (
        // CORRECCIÓN: print:min-h-0 y print:h-auto eliminan la hoja extra
        <div className="min-h-screen bg-gray-100/50 dark:bg-gray-900 pb-12 print:bg-white print:p-0 print:m-0 print:min-h-0 print:h-auto print:pb-0 transition-colors">
            {showEmailModal && <EmailModal quote={quoteData} quoteId={quoteId} onClose={() => setShowEmailModal(false)} setNotification={setNotification} />}

            {/* BARRA SUPERIOR DE ACCIONES (Solo Pantalla) */}
            <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 sticky top-0 z-30 px-4 py-3 shadow-sm print:hidden transition-colors">
                <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setPage('quotes')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-500 dark:text-gray-400">
                            <ArrowLeftIcon className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                                Detalle Cotización
                                <span className={`text-sm font-normal px-2 py-0.5 rounded-md ${quoteData.status === 'Aprobada' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400' : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-400'}`}>
                                    #{quoteId}
                                </span>
                            </h1>
                        </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 justify-end w-full md:w-auto items-center">
                        <label className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 mr-2 transition-colors">
                            <input type="checkbox" checked={includeBankDetails} onChange={(e) => setIncludeBankDetails(e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-800" />
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Datos Bancarios</span>
                        </label>

                        {(isAdmin || canDo('quotes', 'edit')) && quoteData.status === 'Pendiente' && (
                            <button onClick={() => setPage('editQuote', quoteId)} className="flex items-center gap-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/50 transition-colors text-sm font-medium">
                                <PencilSquareIcon className="w-4 h-4" /> <span className="hidden sm:inline">Modificar</span>
                            </button>
                        )}
                         {(isAdmin || canDo('quotes', 'approve')) && quoteData.status === 'Pendiente' && (
                             <button onClick={handleApprove} className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors text-sm font-medium">
                                 <CheckCircleIcon className="w-4 h-4" /> <span className="hidden sm:inline">Aprobar</span>
                             </button>
                         )}
                        {(isAdmin || canDo('quotes', 'delete')) && (
                            <button onClick={handleDelete} className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors text-sm font-medium">
                                <TrashIcon className="w-4 h-4" /> <span className="hidden sm:inline">Eliminar</span>
                            </button>
                        )}
                        <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1 hidden md:block"></div>
                        <button onClick={() => setShowEmailModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 shadow-sm transition-all text-sm font-medium">
                            <EnvelopeIcon className="w-4 h-4" /> Enviar
                        </button>
                        <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-slate-800 dark:bg-slate-700 text-white rounded-lg hover:bg-slate-900 dark:hover:bg-slate-600 shadow-sm transition-all text-sm font-medium">
                            <PrinterIcon className="w-4 h-4" /> Imprimir
                        </button>
                    </div>
                </div>
            </div>

            {/* DOCUMENTO (Diseño Unificado en Pantalla e Impresión) */}
            <div className="max-w-5xl mx-auto mt-6 md:p-8 print:p-4 bg-white dark:bg-gray-800 print:bg-white md:shadow-lg md:rounded-xl md:border md:border-gray-200 dark:md:border-gray-700 print:shadow-none print:border-0 print:m-0 print:w-full print:max-w-none print:mt-0 transition-colors">
                
                {/* MARCA DE AGUA CORREGIDA: Z-50 para estar encima de los fondos blancos */}
                {watermarkUrl && (
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-50 overflow-hidden print:fixed print:inset-0">
                        <img src={watermarkUrl} className="w-[80%] opacity-[0.03] dark:opacity-10 print:opacity-[0.03] transform -rotate-45" alt="Marca de agua" />
                    </div>
                )}

                <div className="relative z-10 p-6 md:p-0 print:p-0">
                    {/* HEADER */}
                    <div className="flex justify-between border-b-2 border-gray-800 dark:border-gray-500 print:border-gray-800 pb-6 mb-8 items-start transition-colors">
                        <div className="flex items-center gap-6">
                            {logoUrl ? (
                                <img src={logoUrl} className="h-20 w-auto object-contain bg-white rounded p-1" alt="Logo"/>
                            ) : (
                                <div className="h-20 w-20 bg-gray-200 dark:bg-gray-700 print:bg-gray-200 flex items-center justify-center rounded text-xs text-gray-500 dark:text-gray-400 print:text-gray-500">LOGO</div>
                            )}
                            <div className="text-xs text-gray-600 dark:text-gray-400 print:text-gray-600 space-y-1 hidden sm:block print:block">
                                <p className="font-bold text-gray-800 dark:text-gray-200 print:text-gray-800 text-sm uppercase">{displayCompanyName}</p>
                                <p className="whitespace-pre-line">{companyAddress || parameters.headerInfo}</p>
                                <p>{companyEmail}</p>
                                <p>{companyPhone}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <h1 className="text-2xl md:text-3xl print:text-3xl font-extrabold text-gray-900 dark:text-gray-100 print:text-gray-900 tracking-tight">COTIZACIÓN</h1>
                            <div className="mt-2 inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-700 print:bg-gray-100 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-600 print:border-gray-200 transition-colors">
                                <span className="text-gray-500 dark:text-gray-400 print:text-gray-500 text-xs font-bold uppercase">Folio</span>
                                <span className="text-gray-900 dark:text-gray-100 print:text-gray-900 font-mono font-bold">{quoteId}</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* CONTENIDO */}
                    <QuoteContent 
                        quote={quoteData} 
                        quoteId={quoteId} 
                        parameters={parameters} 
                        itemsWithCalculatedPrice={itemsWithCalculatedPrice}
                        includeBankDetails={includeBankDetails}
                    />
                    
                    {/* FOOTER */}
                     <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700 print:border-gray-200 flex flex-col items-center text-center gap-2 print:mt-8 transition-colors">
                        <p className="text-xs text-gray-400 dark:text-gray-500 print:text-gray-500 whitespace-pre-line">{parameters.footerInfo}</p>
                        <p className="text-[10px] text-gray-300 dark:text-gray-600 print:text-gray-400">Generado el {new Date().toLocaleDateString()}</p>
                     </div>
                </div>
            </div>
        </div>
    );
};

export default ViewQuotePage;